import React from 'react';
import { useLocation } from 'react-router-dom';
import { tokenStorage } from 'shared/api/storage';
import { companySettingsCache } from '../storage/companySettingsCache';
import { companySettingsApi } from '../api/companySettingsApi';

export const CompanySettingsContext = React.createContext(null);

function isPublicPath(p) {
  return (
    p.startsWith('/rsvp/') ||
    p === '/sign-in' ||
    p === '/sign-up' ||
    p === '/forgot-password' ||
    p === '/verify-email' ||
    p === '/login' ||
    p === '/register'
  );
}

export function CompanySettingsProvider({ children }) {
  const loc = useLocation();

  const [settings, setSettings] = React.useState(() => companySettingsCache.get());
  const [loading, setLoading] = React.useState(!settings);
  const [error, setError] = React.useState(null);

  const clear = React.useCallback(() => {
    companySettingsCache.clear();
    setSettings(null);
    setError(null);
    setLoading(false);
  }, []);

  const refresh = React.useCallback(async () => {
    // ✅ Public sayfalarda settings çekme (RSVP burada)
    if (isPublicPath(loc.pathname)) {
      setLoading(false);
      setError(null);
      // İstersen burada settings'i koruyabilirsin; ben sade olsun diye null'lıyorum:
      // setSettings(null);
      return null;
    }

    const tokens = tokenStorage.get();
    if (!tokens?.accessToken) {
      clear();
      return null;
    }

    setLoading(true);
    setError(null);
    try {
      const dto = await companySettingsApi.getMy();
      setSettings(dto);
      companySettingsCache.set(dto);
      return dto;
    } catch (e) {
      setError(e?.message || 'Company settings fetch failed');
      // ❌ burada tokenStorage.clear yapma (global auth zincirini tetikleme)
      throw e;
    } finally {
      setLoading(false);
    }
  }, [clear, loc.pathname]);

  // ✅ mount + route değişiminde çalışır (loc.pathname dependency yüzünden)
  React.useEffect(() => {
    refresh();
  }, [refresh]);

  // ✅ auth değişince (login/logout) refresh
  React.useEffect(() => {
    const onAuthChanged = () => refresh();
    window.addEventListener('solis:auth:changed', onAuthChanged);
    return () => window.removeEventListener('solis:auth:changed', onAuthChanged);
  }, [refresh]);

  const value = React.useMemo(
    () => ({ settings, loading, error, refresh, clear }),
    [settings, loading, error, refresh, clear]
  );

  return (
    <CompanySettingsContext.Provider value={value}>{children}</CompanySettingsContext.Provider>
  );
}
